// useHotkey is similar to useKeypress, but it only fires when the modifier keys
// state (ctrl, alt, shift, meta) exactly match the given modifiers.
// Usage: useHotkey("[modifiers][+]<key>", callback)
// Case insensitive. Must put all modifiers first.

import { useCallback, useMemo } from "react";

import { IS_NODE } from "@/__main__/constants.mjs";
import { devWarn } from "@/util/dev.mjs";
import getOSType from "@/util/get-os-type.mjs";
import globals from "@/util/global-whitelist.mjs";
import useKeypress from "@/util/use-key-press.mjs";
import type { ValueOf } from "ts-essentials";

const MODIFIERS = {
  Alt: "altKey",
  Ctrl: "ctrlKey",
  Meta: "metaKey",
  Shift: "shiftKey",
} as const;

export const USE_META_AS_CTRL = getOSType() === "darwin";

// Similar to useKeypress, but this one validates modifier state
export default function useHotkey(
  key: string,
  action: (e: KeyboardEvent) => void,
  deps: React.DependencyList,
) {
  const [keyName, keyProps] = useMemo(() => {
    // Get the target key (all modifiers here)
    const [keyName, ...modifiers] = key.split("+").reverse();
    const keyProps = Object.fromEntries(
      Object.values(MODIFIERS).map((k) => [k, false]),
    ) as Record<ValueOf<typeof MODIFIERS>, boolean>;

    for (const modifier of modifiers) {
      if (MODIFIERS[modifier as keyof typeof MODIFIERS]) {
        keyProps[MODIFIERS[modifier as keyof typeof MODIFIERS]] = true;
        continue;
      }

      devWarn(`useHotkey: Unknown modifier ${modifier}`);
    }

    // Swap ctrl and meta key for Mac
    if (USE_META_AS_CTRL) {
      const tmpOldCtrl = keyProps.ctrlKey;
      keyProps.ctrlKey = keyProps.metaKey;
      keyProps.metaKey = tmpOldCtrl;
    }

    return [keyName, keyProps];
  }, [key]);

  const onHotkey = useCallback(action, deps);

  useKeypress(
    keyName,
    (e: KeyboardEvent) => {
      if (!onHotkey) return;

      // for loop returns early if any one prop fails, prefer for over some
      for (const propName in keyProps) {
        if (
          keyProps[propName as keyof typeof keyProps] !==
          e[propName as keyof typeof keyProps]
        )
          return; // strictly match modifier state
      }

      onHotkey(e);
    },
    [onHotkey],
  );
}

/** noop on node */
export function listenHotkey(
  key: string,
  onHotkey: (e: KeyboardEvent) => boolean | void,
) {
  if (IS_NODE) return;

  const [keyName, ...modifiers] = key.split("+").reverse();
  const keyProps = Object.fromEntries(
    Object.values(MODIFIERS).map((k) => [k, false]),
  ) as Record<ValueOf<typeof MODIFIERS>, boolean>;

  for (const modifier of modifiers) {
    if (MODIFIERS[modifier as keyof typeof MODIFIERS]) {
      keyProps[MODIFIERS[modifier as keyof typeof MODIFIERS]] = true;
      continue;
    }

    devWarn(`useHotkey: Unknown modifier ${modifier}`);
  }

  // Swap ctrl and meta key for Mac
  if (USE_META_AS_CTRL) {
    const tmpOldCtrl = keyProps.ctrlKey;
    keyProps.ctrlKey = keyProps.metaKey;
    keyProps.metaKey = tmpOldCtrl;
  }

  const listenerCb = (e: KeyboardEvent) => {
    for (const propName in keyProps) {
      if (
        keyProps[propName as keyof typeof keyProps] !==
        e[propName as keyof typeof keyProps]
      )
        return; // strictly match modifier state
    }

    if ((keyName === e.key || keyName === e.code) && !e.repeat) {
      const consume = onHotkey(e);
      if (consume) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  globals.addEventListener("keydown", listenerCb);
  return () => globals.removeEventListener("keydown", listenerCb);
}
